import React from "react";
import {
    FaArrowRight, FaCheckCircle,
    FaCamera, FaShieldAlt, FaGlobe, FaBookmark,
    FaEye, FaCommentSlash, FaPlane, FaImages,
} from "react-icons/fa";
import HeaderAny from "../../components/header";
import MobileNav from "../../components/header-mobile";
import Footer from "../../components/footer";
import buildingImg from "../../assets/dsp-hero-site.jpg";
import "./solutions.css";
import "./diaspora.css";

const GET_STARTED_URL = "https://web.conra.net";
const PROJECT_CODE = "KN21G";

const PROBLEMS = [
    { num: "01", Icon: FaImages, title: "The “curated” update", desc: "Receiving only the best angles while structural delays, poor workmanship, or long stretches of zero activity stay quietly out of frame." },
    { num: "02", Icon: FaEye, title: "Recycled media", desc: "Standard images have no context. There's no reliable way to prove a photo wasn't taken months ago, or pulled from an entirely different development." },
    { num: "03", Icon: FaCommentSlash, title: "Relationship friction", desc: "Constantly nagging relatives or builders for updates creates awkward tension — and slowly strains personal relationships you actually care about." },
    { num: "04", Icon: FaPlane, title: "The flight penalty", desc: "Investors feel forced to spend thousands on flights just to physically stand on a plot and confirm that a foundation was actually poured." },
];

const CONRA_WAY = [
    { num: "01", title: "A dedicated site window", desc: "A secure, live timeline accessed via a single project code — letting you check progress on your own schedule, from any time zone, without having to message anyone." },
    { num: "02", title: "Built to Be Trusted", desc: "Every image records exactly when and where it was taken, helping you verify that the evidence is genuine and has not been secretly altered." },
    { num: "03", title: "Photos must be captured live", desc: "Photos are taken directly through the Conra app on site. Uploading from a gallery or editing afterward isn't part of the record." },
    { num: "04", title: "Zero app friction for you", desc: "No download needed. Access the project from any browser, bookmark it, and get updates by WhatsApp or email." },
];

const COMPARE_ROWS = [
    { label: "DELIVERY MEDIUM", traditional: "Chaotic WhatsApp threads and heavy emails.", conra: "One clean, browser-based link and code." },
    { label: "USER ACCESS", traditional: "Scrolling through endless media galleries.", conra: "One-click bookmarking on the web." },
    { label: "ALERT SYSTEM", traditional: "Manual messaging across time zones.", conra: "Instant WhatsApp and email alerts." },
    { label: "PROOF OF LOCATION", traditional: "None — relies entirely on verbal trust.", conra: "GPS coordinates locked at capture." },
    { label: "PROOF OF DATE & TIME", traditional: "Easily manipulated or backdated.", conra: "Cryptographically sealed at capture." },
];

const STEPS = [
    { Icon: FaCamera, num: "STEP 01", title: "Capture", desc: "The on-site team or local supervisor snaps progress photos using the ConRa app as milestones are hit." },
    { Icon: FaShieldAlt, num: "STEP 02", title: "Verify", desc: "ConRa locks in the location, timestamp, and metadata at the moment of capture, sealing the image against tampering." },
    { Icon: FaGlobe, num: "STEP 03", title: "Publish", desc: "The verified media automatically populates the project's digital timeline. No manual formatting or delays." },
    { Icon: FaBookmark, num: "STEP 04", title: "Bookmark & track", desc: "Enter your project code, hit “Save” and get instant WhatsApp or email updates the moment a new update drops." },
];


const DiasporaPage = () => (
    <div>
        <div className="web-only"><HeaderAny /></div>
        <div className="mobile-only"><MobileNav /></div>

        {/* ── Hero ── */}
        <section className="dsp-hero">
            <div className="dsp-hero-inner">
                <div className="dsp-hero-text">
                    <span className="bnk-hero-who">For international &amp; diaspora investors</span>
                    <h1 className="dsp-hero-h1">
                        From distance-based anxiety to verifiable certainty.
                    </h1>
                    <p className="dsp-hero-sub">
                        Sending money across borders for a build shouldn't mean being left in the dark. Conra gives you a verified window onto your project, no matter where in the world you're checking from.
                    </p>
                    <div className="dsp-hero-actions">
                        <button className="filled-button" onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}>
                            See a live project <FaArrowRight style={{ marginLeft: 6 }} />
                        </button>
                        <button
                            className="sol-outlined-button"
                            onClick={() => document.getElementById("dsp-problem")?.scrollIntoView({ behavior: "smooth" })}
                        >
                            See the problem it solves
                        </button>
                    </div>
                </div>
                <div className="dsp-hero-visual">
                    <div className="dsp-hero-img-wrap">
                        <span className="dsp-hero-blob" aria-hidden="true" />
                        <img src={buildingImg} alt="Construction team reviewing verified site progress" className="dsp-hero-img" />
                        <span className="dsp-hero-badge">
                            <span className="dsp-hero-badge-dot" />
                            VERIFIED &middot; {PROJECT_CODE} &middot; DAY 84 &middot; WALLING
                        </span>
                    </div>
                </div>
            </div>
        </section>

        {/* ── Problem ── */}
        <section className="dsp-problem" id="dsp-problem">
            <div className="dsp-problem-inner">
                <p className="dsp-overtitle">THE REMOTE TRACKING BLIND SPOT</p>
                <h2 className="dsp-problem-h2">
                    Unverified updates are the biggest risk when you're thousands of miles away.
                </h2>
                <p className="dsp-problem-sub">
                    Managing a build from another country runs into the same problems, almost every time.
                </p>
                <div className="dsp-problem-grid">
                    {PROBLEMS.map(({ num, title, desc }) => (
                        <div key={num} className="dsp-problem-card">
                            <span className="dsp-problem-num">{num}</span>
                            <h4 className="dsp-problem-card-title">{title}</h4>
                            <p className="dsp-problem-card-desc">{desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* ── The ConRa Way ── */}
        <section className="dsp-way">
            <div className="dsp-way-inner">
                <div className="dsp-way-text">
                    <p className="dsp-overtitle dsp-overtitle--left">THE CONRA WAY</p>
                    <h2 className="dsp-way-h2">
                        A dedicated site window, opened by one project code.
                    </h2>
                    <p className="dsp-way-sub">
                        Stay connected without adding to the tools and conversations you already manage. A secure project timeline keeps everything in one place, available when you need it, with timely notifications whenever something new happens.
                    </p>
                </div>
                <div className="dsp-way-list">
                    {CONRA_WAY.map(({ num, title, desc }) => (
                        <div key={num} className="dsp-way-item">
                            <span className="dsp-way-num">{num}</span>
                            <div>
                                <h4 className="dsp-way-item-title">{title}</h4>
                                <p className="dsp-way-item-desc">{desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* ── Compare ── */}
        <section className="dsp-compare-wrap">
            <div className="dsp-compare-inner">
                <span className="dsp-compare-badge">OLD VS. NEW</span>
                <h2 className="dsp-compare-h2">ConRa vs. traditional remote tracking.</h2>
                <p className="dsp-compare-sub">
                    Side by side, on the things that actually matter when you're checking your build from
                    another continent.
                </p>
                <div className="dsp-table">
                    <div className="dsp-table-header">
                        <div className="dsp-table-label-col" />
                        <div className="dsp-table-col-head dsp-col-old">Traditional</div>
                        <div className="dsp-table-col-head dsp-col-new">ConRa</div>
                    </div>
                    {COMPARE_ROWS.map(({ label, traditional, conra }, i) => (
                        <div key={label} className={`dsp-table-row ${i % 2 === 1 ? "dsp-row-alt" : ""}`}>
                            <div className="dsp-table-label">{label}</div>
                            <div className="dsp-table-cell dsp-cell-old">{traditional}</div>
                            <div className="dsp-table-cell dsp-cell-new">
                                <FaCheckCircle className="dsp-cell-check" />
                                <span>{conra}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* ── How It Works ── */}
        <section className="dsp-steps-wrap">
            <div className="dsp-steps-inner">
                <p className="dsp-overtitle">HOW IT WORKS</p>
                <h2 className="dsp-steps-h2">
                    Four steps between shutter click and your bookmark.
                </h2>
                <div className="dsp-steps-grid">
                    {STEPS.map(({ Icon, num, title, desc }, i) => (
                        <div key={num} className="dsp-step">
                            <div className={`dsp-step-photo dsp-step-photo--${i % 4}`}><Icon /></div>
                            <span className="dsp-step-num">{num}</span>
                            <h4 className="dsp-step-title">{title}</h4>
                            <p className="dsp-step-desc">{desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* ── Security & Privacy ── */}
        <section className="dsp-sec-wrap">
            <div className="dsp-sec-inner">
                <p className="dsp-overtitle dsp-overtitle--left">SECURITY &amp; PRIVACY</p>
                <h2 className="dsp-sec-h2">Private by design. Controlled by your project code.</h2>
                <p className="dsp-sec-body">
                    Your project data is handled with care. ConRa is designed with the Kenya Data Protection
                    Act, 2019 and the Office of the Data Protection Commissioner guidelines in mind — consent,
                    permissioned access, and data residency are part of how the platform is built, not
                    add-ons.
                </p>
                <p className="dsp-sec-body">
                    Access is completely private. Your timeline is never publicly searchable. Only individuals
                    holding your specific project code can view it.
                </p>
                <div className="dsp-sec-badges">
                    <span className="dsp-sec-badge"><strong>C2PA</strong> &mdash; active member</span>
                    <span className="dsp-sec-badge"><strong>KGBS</strong> &mdash; active member</span>
                    <span className="dsp-sec-badge"><strong>KIPI</strong> &mdash; provisional patent filed</span>
                </div>
            </div>
        </section>

        {/* ── Not banner ── */}
        {/* <section className="pub-not-banner">
                <div className="pub-not-banner-inner">
                    <p className="pub-not-banner-text">
                        Conra doesn't manage your build or hold your money. It gives you proof of what's actually happening on it.
                    </p>
                </div>
            </section> */}

        {/* ── CTA ── */}
        <section className="dsp-cta-wrap" id="contact">
            <div className="dsp-cta-inner">
                <h2 className="dsp-cta-h2">Start with one project code.</h2>
                <p className="dsp-cta-sub">
                    Bookmark a live project and see what verified, tamper-evident visibility into your
                    investment actually looks like — from anywhere in the world.
                </p>
                <div className="dsp-cta-actions">
                    <button className="filled-button" onClick={() => window.open(GET_STARTED_URL, "_blank")}>
                        See a live project
                    </button>
                    <a
                        className="dsp-cta-btn-outline"
                        href="mailto:hello@conra.net?subject=Conra%20-%20Diaspora%20investor"
                    >
                        Talk to our team
                    </a>
                </div>
            </div>
        </section>

        <Footer />
    </div>
);

export default DiasporaPage;
